import type { Building } from "building-types/building/buildingTypes";
import type { WallStructuralDomain } from "building-types/structural/structuralDomainTypes";
import type { RWTable } from "common/data/table";
import uid from "common/utils/uid";
import {
  propagateHorizontalDomains,
  type LevelIndexByLevelId,
  type WallStructuralDomainSettings,
  candidatesByPlane,
  candidatesByPlaneBottomElevAssemblyCondition,
  candidatesByPlaneAssemblyCondition,
} from "../generationTools/generateWallStructuralDomainTools";
import { wallStructuralDomainNew } from "../generationTools/wallStructuralDomainTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

// @todo(Andrew): this should be a Wall Structure Settings object
const settings: WallStructuralDomainSettings = {
  breakAtBottom: true,
  breakAtTop: true,
  breakAtXYDomain: true,
  breakAtGarageCondition: true,
  trimWithRoofBottomFaces: false,
  trimWithRoofTopFaces: true,
  /**
   * This value is relatively arbitrary. It has been tested with a handful of promotion
   * homes.
   */
  trimFaceOffset: 18,
  splitNonRectangularDomains: true,
};

function select({
  walls,
  roofSystems,
  settings,
  levels,
}: Pick<Building, "walls" | "roofSystems" | "settings" | "levels">) {
  return {
    walls,
    roofSystems,
    wallCatalog: settings.wallCatalog,
    levels,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  walls: "shallow",
  roofSystems: "shallow",
  wallCatalog: "shallow",
  levels: "shallow",
});

function generate({
  walls,
  roofSystems,
  wallCatalog,
  levels,
}: ReturnType<typeof select>): Pick<Building, "wallStructuralDomains"> {
  const newWallStructuralDomains: RWTable<WallStructuralDomain> = {};

  const levelIndexById: LevelIndexByLevelId = {};
  for (const levelId in levels) {
    levelIndexById[levelId] = levels[levelId].floorIndex;
  }
  const maxLevelIndex = Math.max(...Object.values(levelIndexById));

  const byPlane = candidatesByPlane(walls, roofSystems, levelIndexById, wallCatalog, maxLevelIndex, settings);

  const propagated = propagateHorizontalDomains(byPlane, settings);

  const byPlaneBottomElevAssembCond = candidatesByPlaneBottomElevAssemblyCondition(settings, propagated, wallCatalog);

  const byPlaneAssembCond = candidatesByPlaneAssemblyCondition(settings, byPlaneBottomElevAssembCond, wallCatalog);

  /**
   * Build wall structural domains from the candidates.
   */
  for (const hash in byPlaneAssembCond) {
    const candidates = byPlaneAssembCond[hash];

    for (const candidate of candidates) {
      const {
        domain,
        plane,
        layers,
        condition,
        leftRoomCondition,
        rightRoomCondition,
        leftRooms,
        rightRooms,
        levelIndex,
        parentHandles,
      } = candidate;

      const generatingLevels = Array.from(candidate.generatingLevels).sort(
        (a, b) => levelIndexById[a.id] - levelIndexById[b.id],
      );
      const bottomGeneratingLevel = generatingLevels.pop();

      const id = uid();
      const wallStructuralDomain: WallStructuralDomain = wallStructuralDomainNew({
        name: hash,
        resolvedBoundary: domain,
        plane,
        id,
        layers: Object.values(layers),
        leftRooms: Array.from(leftRooms),
        rightRooms: Array.from(rightRooms),
        condition,
        leftRoomCondition,
        rightRoomCondition,
        levelIndex,
        bottomGeneratingLevel,
        parentHandles: Object.values(parentHandles),
      });

      newWallStructuralDomains[wallStructuralDomain.id] = wallStructuralDomain;
    }
  }

  return {
    wallStructuralDomains: newWallStructuralDomains,
  };
}

export default memo(select, generate, compare);
